// Direct imports
import { ILoadingScreen } from "@babylonjs/core/Loading/loadingScreen";
import { IProgressTracker } from "./ProgressTracker";

// 2024
// Author: Aaron Post

export class CustomLoadingScreen implements ILoadingScreen, IProgressTracker {
    public loadingUIBackgroundColor: string = "transparent";
    private loadingContainer: HTMLDivElement;
    private loadingBar: HTMLDivElement;
    private loadingText: HTMLDivElement;
    private parentContainer: HTMLElement;

    constructor(public loadingUIText: string, parentContainer?: HTMLElement | null) {
        this.parentContainer = parentContainer || document.body;
        // The host page and the scene's clear colour may be light or dark, so
        // the text and bar sit on their own dark, translucent panel.
        this.loadingContainer = document.createElement("div");
        this.loadingContainer.style.cssText = "position:absolute;inset:0;display:flex;align-items:center;"
            + `justify-content:center;background:${this.loadingUIBackgroundColor}`;

        const panel = document.createElement("div");
        panel.style.cssText = "display:flex;flex-direction:column;gap:10px;width:min(240px,70%);padding:14px 18px;"
            + "border-radius:14px;background:rgba(20,20,22,0.72);box-shadow:0 6px 24px rgba(0,0,0,0.18);"
            + "-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px)";

        this.loadingText = document.createElement("div");
        this.loadingText.style.cssText = "color:#fff;font:500 13px/1.3 system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;"
            + "letter-spacing:0.01em;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis";
        this.loadingText.innerText = this.loadingUIText;

        const track = document.createElement("div");
        track.style.cssText = "height:4px;border-radius:2px;overflow:hidden;background:rgba(255,255,255,0.22)";
        this.loadingBar = document.createElement("div");
        this.loadingBar.style.cssText = "height:100%;border-radius:2px;background:#fff;transition:width 0.2s ease";
        this.loadingBar.style.width = "0%";
        track.appendChild(this.loadingBar);

        panel.append(this.loadingText, track);
        this.loadingContainer.appendChild(panel);
        this.parentContainer.appendChild(this.loadingContainer);
    }

    public displayLoadingUI(): void {
        this.parentContainer.appendChild(this.loadingContainer);
    }

    /** Takes the overlay out of the page: every load makes a new one, so a hidden one would stay behind. */
    public hideLoadingUI(): void {
        this.loadingContainer.remove();
    }

    // ============================================
    // IProgressTracker Implementation
    // ============================================

    private lastBarWidth = "";
    private lastText = "";
    private downloadWeight = 0;

    /**
     * Reserve the leading `weight` fraction of the bar for an initial
     * download phase, and get back a byte-progress callback to drive it.
     * The subsequent build phase (onStart/onProgress) fills the remainder,
     * so the bar moves monotonically across download → build.
     */
    public trackDownload(weight: number): (loaded: number, total: number) => void {
        this.downloadWeight = weight;
        return (loaded: number, total: number) => {
            // Content-Length may be missing, wrong, or the compressed size
            // while we count decoded bytes — when `loaded` overshoots (or no
            // total is known), fall back to a ticking byte counter instead of
            // pinning a bogus 100%.
            const mb = (loaded / 1048576).toFixed(1);
            if (total > 0 && loaded <= total) {
                const fraction = loaded / total;
                this.setBar(fraction * weight);
                this.setText(`Downloading… ${Math.round(fraction * 100)}% (${mb} MB)`);
            } else if (total > 0) {
                // Overshoot (compressed transfer): the bar already reached the
                // band end while counts were consistent — park it there.
                this.setBar(weight);
                this.setText(`Downloading… ${mb} MB`);
            } else {
                // No Content-Length: there is no honest fraction to draw, so
                // leave the bar alone and let the byte counter carry liveness.
                this.setText(`Downloading… ${mb} MB`);
            }
        };
    }

    public onStart(total: number): void {
        this.onProgress(0, total);
    }

    public onProgress(loaded: number, total: number): void {
        // Fires per network chunk during nested downloads, with fractional
        // `loaded`. The bar gets the smooth fractional progress mapped into
        // the band after the download phase; the text only ever shows whole
        // counts. Skip DOM writes when nothing visible changed.
        const fraction = total > 0 ? Math.min(1, loaded / total) : 0;
        this.setBar(this.downloadWeight + (1 - this.downloadWeight) * fraction);

        // A count like "0/1" is noise for single-item loads — keep the
        // generic label and let the bar carry the progress. Ceil so the
        // item currently in flight counts ("1/3" while the first nested
        // asset downloads, not a long-lived "0/3").
        this.setText(total > 1
            ? `Loading ${Math.min(Math.max(1, Math.ceil(loaded)), total)}/${total} objects...`
            : this.loadingUIText);
    }

    private setBar(fraction: number): void {
        const barWidth = (fraction * 100).toFixed(1) + "%";
        if (barWidth !== this.lastBarWidth) {
            this.lastBarWidth = barWidth;
            this.loadingBar.style.width = barWidth;
        }
    }

    private setText(text: string): void {
        if (text !== this.lastText) {
            this.lastText = text;
            this.loadingText.innerText = text;
        }
    }

    public onComplete(): void {
        // Optional: Add any completion animations here
    }
}
